import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '@app/hooks/reduxHooks'

import { useDeviceData } from '@/hooks'
import type { ProfileResponse } from '@/api/profile.api'

type DeviceDataContextType = ReturnType<typeof useDeviceData> & {
  clickedChip: undefined | string
  setClickedChip: React.Dispatch<React.SetStateAction<undefined | string>>
  profile: ProfileResponse | null
}

const DevicesContext = React.createContext<DeviceDataContextType | null>(null)

export const DevicesProvider = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation()

  const [clickedChip, setClickedChip] = React.useState<undefined | string>()

  const navigate = useNavigate()
  const profile = useAppSelector((state) => state.profile.data)

  const { visibleDevices, devicesStatuses, telemetryBySerial, setVisibleDevices, setDevicesStatuses } = useDeviceData()

  const channelRef = React.useRef<BroadcastChannel | null>(null)
  const latestRef = React.useRef({
    visibleDevices,
    devicesStatuses,
    clickedChip,
    navigate,
  })

  latestRef.current = {
    visibleDevices,
    devicesStatuses,
    clickedChip,
    navigate,
  }

  const broadcastToDetachedMap = React.useCallback((type: string, payload?: Record<string, unknown>) => {
    const isDetached = localStorage.getItem('isMapDetached') === 'true'
    if (!isDetached || !channelRef.current) return

    try {
      channelRef.current.postMessage({ type, payload })
    } catch (error) {
      console.warn(`Failed to broadcast ${type}:`, error)
    }
  }, [])

  React.useEffect(() => {
    const bc = new BroadcastChannel('map-channel')
    channelRef.current = bc

    const handleMessage = (event: MessageEvent) => {
      const { type, payload } = event.data || {}

      switch (type) {
        case 'REQUEST_FULL_DATA': {
          const current = latestRef.current
          try {
            bc.postMessage({
              type: 'DEVICE_DATA_UPDATED',
              payload: {
                visibleDevices: current.visibleDevices,
                devicesStatuses: current.devicesStatuses,
                clickedChip: current.clickedChip,
              },
            })
          } catch (error) {
            console.warn('Failed to respond to REQUEST_FULL_DATA:', error)
          }
          break
        }
        case 'PIN_CLICKED':
          if (payload?.deviceId) latestRef.current.navigate(`/device/${payload.deviceId}`)
          break
        case 'CHIP_CLICKED':
          setClickedChip(payload.deviceId)
          break
      }
    }

    bc.addEventListener('message', handleMessage)

    return () => {
      bc.removeEventListener('message', handleMessage)
      bc.close()
      channelRef.current = null
    }
  }, [])

  React.useEffect(() => {
    broadcastToDetachedMap('LANGUAGE_CHANGED', { lng: i18n.language })
  }, [broadcastToDetachedMap, i18n.language])

  React.useEffect(() => {
    broadcastToDetachedMap('DEVICE_DATA_UPDATED', {
      visibleDevices,
      devicesStatuses,
      clickedChip,
    })
  }, [broadcastToDetachedMap, clickedChip, devicesStatuses, visibleDevices])

  return (
    <DevicesContext.Provider
      value={{
        visibleDevices,
        devicesStatuses,
        telemetryBySerial,
        setVisibleDevices,
        setDevicesStatuses,
        clickedChip,
        setClickedChip,
        profile,
      }}
    >
      {children}
    </DevicesContext.Provider>
  )
}

export const useDeviceDataContext = () => {
  const context = React.useContext(DevicesContext)
  if (!context) {
    throw new Error('useDeviceDataContext must be used within a DeviceDataProvider')
  }
  return context
}
